import React from 'react';
import { Link } from 'react-router-dom';
import { UserAvatar } from '../../../components/ui';
import { formatPublishedAt, publisherName } from '../release-note-model';
import '../release-notes.css';

// The left column of a feed row (titleAs="h2", titleTo set) and of the detail
// page (titleAs="h1"): date, title, tags, publisher (spec §4, §5). The
// publisher row carries the photo when the note has one (2026-10-21).
const ReleaseNoteMeta = ({
  note,
  titleAs = 'h2',
  titleTo,
  publisherCaption,
  className = '',
}) => {
  const Title = titleAs;
  const date = formatPublishedAt(note?.publishedat);
  const name = publisherName(note);
  // Feed rows (h2) and the detail page (h1) share the column; the detail
  // gets the larger scale and a stronger name (designer brief 2026-10-16).
  const isDetail = titleAs === 'h1';
  const titleCls = isDetail
    ? 'rn-display text-3xl font-semibold leading-tight tracking-tight text-slate-900 dark:text-slate-100 sm:text-4xl'
    : 'rn-display text-2xl font-semibold leading-tight tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl';

  return (
    <div className={`flex min-w-0 flex-col items-start ${isDetail ? 'gap-4' : 'gap-3.5'} ${className}`}>
      {date ? (
        <time
          dateTime={note.publishedat}
          className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400"
        >
          {date}
        </time>
      ) : null}
      <Title className={titleCls} style={{ textWrap: 'balance' }}>
        {titleTo ? (
          <Link to={titleTo} className="hover:text-blue-600 dark:hover:text-sky-400">
            {note.title}
          </Link>
        ) : (
          note.title
        )}
      </Title>
      {note.tags?.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Tags">
          {note.tags.map((tag) => (
            <li
              key={tag}
              className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            >
              {tag}
            </li>
          ))}
        </ul>
      ) : null}
      {name ? (
        <div data-testid="rn-publisher" className={`mt-1 flex min-w-0 items-center ${isDetail ? 'gap-3.5' : 'gap-3'}`}>
          {/* UserAvatar labels itself with the name; hide it so the name is read once. */}
          <span aria-hidden="true" className="shrink-0">
            <UserAvatar name={name} src={note.publisherphotourl || undefined} size={isDetail ? 'xl' : 'md'} />
          </span>
          <div className="flex min-w-0 flex-col">
            <span
              className={
                isDetail
                  ? 'break-words text-base font-semibold leading-snug text-slate-900 dark:text-slate-100'
                  : 'break-words text-sm font-medium text-slate-700 dark:text-slate-200'
              }
            >
              {name}
            </span>
            {publisherCaption ? (
              <span className="text-sm text-slate-500 dark:text-slate-400">{publisherCaption}</span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ReleaseNoteMeta;
